import { useEffect, useState } from 'react';
import { motion, AnimatePresence, useMotionValue, animate } from 'framer-motion';

const REEL_ITEMS = Array.from({ length: 10 }, (_, n) => n);

// A single spinning digit column — mimics a slot-machine reel settling on a value.
const DigitReel = ({ digit }) => (
  <div className="relative h-[0.85em] w-[0.62em] overflow-hidden">
    <motion.div
      animate={{ y: `${-digit * 10}%` }}
      transition={{ type: 'spring', stiffness: 260, damping: 24, mass: 0.9 }}
    >
      {REEL_ITEMS.map((n) => (
        <div key={n} className="flex h-[0.85em] items-center justify-center">
          {n}
        </div>
      ))}
    </motion.div>
  </div>
);

const SplashScreen = ({ onComplete }) => {
  const progressMV = useMotionValue(0);
  const [progress, setProgress] = useState(0);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';

    const unsubscribe = progressMV.on('change', (value) => {
      setProgress(Math.round(value));
    });

    const controls = animate(progressMV, 100, {
      duration: 2.2,
      ease: [0.16, 1, 0.3, 1],
      onComplete: () => {
        setTimeout(() => setExiting(true), 350);
      }
    });

    return () => {
      unsubscribe();
      controls.stop();
      document.body.style.overflow = '';
    };
  }, [progressMV]);

  useEffect(() => {
    if (!exiting) return;
    const timeout = setTimeout(onComplete, 700);
    return () => clearTimeout(timeout);
  }, [exiting, onComplete]);

  const hundreds = Math.floor(progress / 100);
  const tens = Math.floor(progress / 10) % 10;
  const units = progress % 10;

  return (
    <AnimatePresence>
      {!exiting && (
        <motion.div
          key="splash"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.7, ease: 'easeInOut' } }}
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden bg-gradient-to-br from-[#1a0b3d] via-[#2e0f5e] to-[#0c0420]"
        >
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
          <div className="absolute top-1/2 left-1/2 w-[32rem] h-[32rem] -translate-x-1/2 -translate-y-1/2 bg-blue-500/10 rounded-full blur-3xl" />

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="relative mb-8 text-lg md:text-xl font-semibold tracking-[0.3em] text-purple-200 uppercase"
          >
            Stray Apps
          </motion.div>

          <div className="relative flex items-end font-bold text-white text-7xl md:text-9xl tabular-nums">
            {hundreds > 0 && <DigitReel digit={hundreds} />}
            <DigitReel digit={tens} />
            <DigitReel digit={units} />
            <span className="ml-2 text-4xl md:text-6xl bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
              %
            </span>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="relative mt-8 text-sm text-purple-300/70 tracking-widest uppercase"
          >
            connecting dots ...
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SplashScreen;
